/**
 * TextureGenerator - Bakes country polygons into textures for GPU rendering.
 *
 * Proof of Concept: Creates country color and border textures from polygon data.
 */

import * as THREE from "three";

import { GLOBE_CONFIG } from "../constants";

import type { CountryData, LakeData, ProvinceData } from '../types/features'

export class CountryTextureGenerator {
  constructor(
    private width: number = 4096,
    private height: number = 2048
  ) {}

  /**
   * Create a new canvas for texture generation.
   */
  private createCanvas(): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
    const canvas = document.createElement('canvas')
    canvas.width = this.width
    canvas.height = this.height
    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    if (!ctx) throw new Error('Failed to get 2D context')
    return { canvas, ctx }
  }

  /** Ocean color for background and lakes */
  private static readonly OCEAN_COLOR = '#b8d4e8'

  /**
   * Generate country color texture.
   * Each country polygon is filled with its designated color.
   * Lakes are rendered on top as ocean color.
   */
  generateCountryTexture(countries: CountryData[], lakes: LakeData[] = []): THREE.CanvasTexture {
    const { canvas, ctx } = this.createCanvas()

    // Clear canvas with light ocean blue background
    ctx.fillStyle = CountryTextureGenerator.OCEAN_COLOR
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    // Render each country (all polygon parts)
    countries.forEach((country) => {
      ctx.fillStyle = this.rgbToHex(country.color)
      country.polygons.forEach((part) => {
        const path = this.projectPolygonPartToUV(part, canvas)
        ctx.fill(path, 'evenodd') // evenodd for proper hole rendering
      })
    })

    // Render lakes on top as ocean color
    ctx.fillStyle = CountryTextureGenerator.OCEAN_COLOR
    lakes.forEach((lake) => {
      lake.polygons.forEach((part) => {
        const path = this.projectPolygonPartToUV(part, canvas)
        ctx.fill(path, 'evenodd')
      })
    })

    const texture = new THREE.CanvasTexture(canvas)
    texture.colorSpace = THREE.SRGBColorSpace
    texture.minFilter = THREE.LinearFilter
    texture.magFilter = THREE.LinearFilter
    texture.wrapS = THREE.ClampToEdgeWrapping
    texture.wrapT = THREE.ClampToEdgeWrapping
    texture.flipY = false // Don't flip - our UVs match canvas coordinates
    texture.needsUpdate = true

    return texture
  }

  /**
   * Generate border texture.
   * Renders black borders around each country and lake.
   */
  generateBorderTexture(countries: CountryData[], lakes: LakeData[] = []): THREE.CanvasTexture {
    const { canvas, ctx } = this.createCanvas()

    // Clear to transparent
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    // Render borders for all country polygon parts
    ctx.strokeStyle = 'black'
    ctx.lineWidth = 1
    countries.forEach(country => {
      country.polygons.forEach((part) => {
        const path = this.projectPolygonPartToUV(part, canvas)
        ctx.stroke(path)
      })
    })

    // Render borders for lakes
    ctx.lineWidth = 0.7
    lakes.forEach(lake => {
      lake.polygons.forEach((part) => {
        const path = this.projectPolygonPartToUV(part, canvas)
        ctx.stroke(path)
      })
    })

    const texture = new THREE.CanvasTexture(canvas)
    texture.flipY = false // Don't flip - our UVs match canvas coordinates
    texture.needsUpdate = true
    return texture
  }

  /** Province border color (gray, lighter than country borders) */
  private static readonly PROVINCE_BORDER_COLOR = '#666666'

  /** Province border line width (thinner than country borders) */
  private static readonly PROVINCE_BORDER_WIDTH = 0.5

  /**
   * Generate province border texture.
   * Renders gray borders for provinces/states at higher zoom levels.
   */
  generateProvinceBorderTexture(provinces: ProvinceData[]): THREE.CanvasTexture {
    const { canvas, ctx } = this.createCanvas()

    // Clear to transparent
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    // Render borders for all province polygon parts
    ctx.strokeStyle = CountryTextureGenerator.PROVINCE_BORDER_COLOR
    ctx.lineWidth = CountryTextureGenerator.PROVINCE_BORDER_WIDTH
    provinces.forEach((province) => {
      province.polygons.forEach((part) => {
        const path = this.projectPolygonPartToUV(part, canvas)
        ctx.stroke(path)
      })
    })

    const texture = new THREE.CanvasTexture(canvas)
    texture.flipY = false // Don't flip - our UVs match canvas coordinates
    texture.needsUpdate = true
    return texture
  }

  /** Glow blur radius in pixels for emboss effect */
  private static readonly GLOW_BLUR_RADIUS = 8

  /**
   * Generate glow texture for emboss effect.
   * White (1.0) at borders, fading to black (0.0) away from borders.
   * Uses canvas shadowBlur for natural Gaussian-like falloff.
   */
  generateGlowTexture(countries: CountryData[]): THREE.CanvasTexture {
    const { canvas, ctx } = this.createCanvas()

    // Start with black (no glow)
    ctx.fillStyle = 'black'
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    // Draw white glow around borders using shadow blur
    ctx.shadowColor = 'white'
    ctx.shadowBlur = CountryTextureGenerator.GLOW_BLUR_RADIUS
    ctx.shadowOffsetX = 0
    ctx.shadowOffsetY = 0
    ctx.strokeStyle = 'white'
    ctx.lineWidth = 1

    countries.forEach((country) => {
      country.polygons.forEach((part) => {
        const path = this.projectPolygonPartToUV(part, canvas)
        ctx.stroke(path)
      })
    })

    const texture = new THREE.CanvasTexture(canvas)
    texture.flipY = false
    texture.colorSpace = THREE.LinearSRGBColorSpace // Grayscale, linear for shader math
    texture.minFilter = THREE.LinearFilter
    texture.magFilter = THREE.LinearFilter
    texture.wrapS = THREE.ClampToEdgeWrapping
    texture.wrapT = THREE.ClampToEdgeWrapping
    texture.needsUpdate = true
    return texture
  }

  /**
   * Generate land mask texture.
   * White (1.0) for land, black (0.0) for ocean/lakes.
   */
  generateLandMaskTexture(countries: CountryData[], lakes: LakeData[] = []): THREE.CanvasTexture {
    const { canvas, ctx } = this.createCanvas()

    // Clear to black (ocean)
    ctx.fillStyle = 'black'
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    // Fill all land areas with white
    ctx.fillStyle = 'white'
    countries.forEach(country => {
      country.polygons.forEach((part) => {
        const path = this.projectPolygonPartToUV(part, canvas)
        ctx.fill(path, 'evenodd')
      })
    })

    // Cut out lakes (back to black/ocean)
    ctx.fillStyle = 'black'
    lakes.forEach(lake => {
      lake.polygons.forEach((part) => {
        const path = this.projectPolygonPartToUV(part, canvas)
        ctx.fill(path, 'evenodd')
      })
    })

    const texture = new THREE.CanvasTexture(canvas)
    texture.flipY = false
    texture.needsUpdate = true
    return texture
  }

  /**
   * Generate country ID texture for picking.
   * Each country is encoded with a unique RGB color representing its index.
   */
  generateIDTexture(countries: CountryData[]): THREE.DataTexture {
    const { canvas, ctx } = this.createCanvas()

    // Clear to black
    ctx.fillStyle = 'black'
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    // Render each country with encoded ID (all polygon parts get same ID)
    countries.forEach((country, index) => {
      // Encode index as RGB (supports up to 16M countries)
      const r = index % 256
      const g = Math.floor(index / 256) % 256
      const b = Math.floor(index / 65536)

      ctx.fillStyle = `rgb(${r}, ${g}, ${b})`
      country.polygons.forEach((part) => {
        const path = this.projectPolygonPartToUV(part, canvas)
        ctx.fill(path, 'evenodd')
      })
    })

    // Extract RGB data
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
    const data = new Uint8Array(canvas.width * canvas.height * 3)

    // Copy RGB channels only (skip alpha)
    for (let i = 0; i < imageData.data.length / 4; i++) {
      data[i * 3] = imageData.data[i * 4]
      data[i * 3 + 1] = imageData.data[i * 4 + 1]
      data[i * 3 + 2] = imageData.data[i * 4 + 2]
    }

    return new THREE.DataTexture(
      data,
      canvas.width,
      canvas.height,
      THREE.RGBFormat
    )
  }

  /**
   * Project a polygon ring to UV canvas coordinates.
   */
  private projectRingToPath(ring: number[][], canvas: HTMLCanvasElement, path: Path2D): void {
    ring.forEach(([lng, lat], i) => {
      // Convert lat/lng to UV coordinates (0-1 range)
      const u = (lng + 180) / 360
      const v = (GLOBE_CONFIG.MAX_LATITUDE - lat) /
                (GLOBE_CONFIG.MAX_LATITUDE - GLOBE_CONFIG.MIN_LATITUDE)

      // Scale to canvas pixel coordinates
      const x = u * canvas.width
      const y = v * canvas.height

      if (i === 0) {
        path.moveTo(x, y)
      } else {
        path.lineTo(x, y)
      }
    })
    path.closePath()
  }

  /**
   * Project geographic polygon part (with optional holes) to UV canvas coordinates.
   */
  private projectPolygonPartToUV(
    part: { exterior: number[][]; holes?: number[][][] },
    canvas: HTMLCanvasElement
  ): Path2D {
    const path = new Path2D()

    // Add exterior ring
    this.projectRingToPath(part.exterior, canvas, path)

    // Add hole rings (they will be subtracted with evenodd fill rule)
    if (part.holes) {
      part.holes.forEach((hole) => {
        this.projectRingToPath(hole, canvas, path)
      })
    }

    return path
  }

  /**
   * Convert RGB array (0-1 range) to hex color string.
   */
  private rgbToHex([r, g, b]: [number, number, number]): string {
    const toHex = (n: number) =>
      Math.round(n * 255).toString(16).padStart(2, '0')
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`
  }
}
